import startsWith from 'lodash/startsWith';

// service
import appLogger from 'app/shared/loggers/appLogger';

const queryUtil = {
    /*
     * Remove multiple instances question marks (?) in query param
     *
     * @param {string} queryParam - the query parameter
     * @returns {string}
     */
    cleanQueryParams(queryParam) {
        return queryParam.replace(/\?/g, '&').replace('&', '?');
    },

    isAlphaNumeric(ch) {
        return ch.match(/^[a-z0-9]+$/i) !== null;
    },

    /*
     * examples:
     * www.url.com/ -> www.url.com
     * www.url.com/?foo=bar -> www.url.com?foo=bar
     * www.url.com/?foo=bar/ -> www.url.com?foo=bar
     */
    parseExtraSlashes(url) {
        const regex = new RegExp('\\/\\?|\\/\\&', 'g');
        let statusCode = 200;

        if (url.match(regex)) {
            url = url.replace(regex, '?');
            statusCode = 301;
        }

        if (url.substr(url.length - 1) === '/') {
            url = url.slice(0, -1);
            statusCode = 301;
        }

        return {
            statusCode,
            url,
        };
    },

    /*
     * Take a path and parse that into path and query params
     *
     * @param {string} the path + query params
     * @returns {obj} {path, queryParams}
     */
    parsePathAndQueryParams(fullPath) {
        fullPath = this.cleanQueryParams(fullPath);
        const parts = fullPath.split('?');
        const path = parts[0];
        let queryParams = '';

        if (parts.length > 1) {
            queryParams = this.stringify(this.parse(parts[1]));
        }

        return {
            path,
            queryParams,
        };
    },

    sanitize(obj = {}) {
        const newQueryObj = {};

        for (const key in obj) {
            if (obj.hasOwnProperty(key) && obj[key]) {
                // remove everything past ?
                if (obj[key].lastIndexOf('?') > -1) {
                    obj[key] = obj[key].substr(0, obj[key].lastIndexOf('?'));
                }
                newQueryObj[encodeURIComponent(key)] = encodeURIComponent(obj[key]);
            }
        }

        return newQueryObj;
    },
    stringify(obj) {
        const str = [];
        let p;

        for (p in obj) {
            if (obj.hasOwnProperty(p) && (obj[p] || obj[p] === false)) {
                str.push(`${encodeURIComponent(p)}=${encodeURIComponent(obj[p])}`);
            }
        }

        if (str.length > 0) {
            return `?${str.join('&')}`;
        }
        return '';
    },
    parse(string = '') {
        if (string === '') {
            return {};
        }

        const queryObj = {};
        let pieces = string;

        if (startsWith(pieces, '?')) {
            pieces = pieces.split('?')[1];
        }
        pieces = pieces.split('&');

        pieces.forEach((piece) => {
            const splitPiece = piece.split('=');
            const name = splitPiece[0];
            const value = splitPiece[1];

            try {
                queryObj[name] = decodeURIComponent(value);
            } catch (error) {
                const errorMsg = `ERROR @ queryUtils.parse: ${error}`;

                appLogger.logError({
                    message: errorMsg,
                    context: 'queryUtils.parse',
                });
            }
        });

        return queryObj;
    },
    encodeQueryString(string) {
        return this.stringify(this.parse(string));
    },

    /*
     * Return path with query parameters we want to persist
     *
     * @param {string} origPath - The original path
     * @param {string} locationSearch - The query parameters
     * @param {array} tempWhiteList - A list to append to the current query param white list
     * @returns {string}
     */
    getPathWithPresistedQueryParams(origPath, locationSearch = '', tempWhiteList = []) {
        locationSearch = this.cleanQueryParams(locationSearch);

        // list of query params we want to persist as the user navigates
        const QUERY_PARAM_WHITE_LIST = [
            'utm_source',
            'utm_medium',
            'utm_campaign',
            'itc',
        ].concat(tempWhiteList);

        const queryParamObj = this.parse(locationSearch);

        // loop through query params
        for (const queryParam in queryParamObj) {
            // check if query params if in the white list
            if (QUERY_PARAM_WHITE_LIST.indexOf(queryParam) < 0) {
                // did not find match - remove queryParam
                delete queryParamObj[queryParam];
            }
        }

        // separate path and query params as an object
        const queryParamsString = this.stringify(queryParamObj);
        const tempObj = this.parsePathAndQueryParams(`${origPath}${queryParamsString}`);

        return `${tempObj.path}${tempObj.queryParams}`;
    },
};

export default queryUtil;
